/**
 * Google Search Service
 * Handles Google Custom Search API logic via backend proxy
 * Extracted from geminiService.ts for better performance and maintainability
 *
 * This service maintains the semantic search approach:
 * - Uses full topic phrases (not keyword fragmentation)
 * - Domain context validation to prevent false matches
 * - Credible source filtering with Philippine K-12 curriculum optimization
 * - Semantic reranking using HF BGE-Reranker-Base
 *
 * Pipeline: Google Search API (Backend Proxy) → HF Reranker → Gemini
 */

import { logDebug, logWarn, logError, logInfo, logSuccess } from "@/utils/secureLogger";
import { sanitizeSearchQuery } from "@/utils/contentSafetyFilter";

/**
 * Search result interface
 */
export interface SearchResult {
  url: string;
  title?: string;
  snippet?: string;
}

/**
 * Google Search Service Configuration
 */
interface GoogleSearchConfig {
  apiKey: string;
  searchEngineId: string;
}

/**
 * Service for handling Google Custom Search via backend proxy
 * Optimized for Philippine K-12 educational content
 */
export class GoogleSearchService {
  private isInitialized = false;
  private config: GoogleSearchConfig;

  // ✅ OPTIMIZATION: Response cache for repeated topics (5-minute TTL)
  private searchCache = new Map<string, { sources: string[]; timestamp: number }>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly MAX_CACHE_SIZE = 50; // Maximum number of cache entries

  constructor(config: GoogleSearchConfig) {
    this.config = config;
  }

  /**
   * ✅ IMPROVED: LRU-style cache eviction (Least Recently Used)
   * Removes oldest entries when cache exceeds limit
   */
  private evictOldestCacheEntries(): void {
    if (this.searchCache.size <= this.MAX_CACHE_SIZE) {
      return;
    }

    // Sort by timestamp (oldest first)
    const sortedEntries = Array.from(this.searchCache.entries()).sort(
      (a, b) => a[1].timestamp - b[1].timestamp
    );

    // Remove oldest 25% when limit exceeded
    const entriesToRemove = Math.ceil(this.searchCache.size * 0.25);
    for (let i = 0; i < entriesToRemove; i++) {
      const [key] = sortedEntries[i];
      this.searchCache.delete(key);
    }

    logDebug(`🗑️ Cache eviction: removed ${entriesToRemove} oldest entries`, {
      newSize: this.searchCache.size,
      maxSize: this.MAX_CACHE_SIZE,
    });
  }

  /**
   * ✅ IMPROVED: Proactive TTL-based cache cleanup
   * Removes ALL expired entries during cache operations
   */
  private cleanExpiredCacheEntries(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];

    for (const [key, value] of this.searchCache.entries()) {
      if (now - value.timestamp >= this.CACHE_TTL) {
        expiredKeys.push(key);
      }
    }

    if (expiredKeys.length > 0) {
      expiredKeys.forEach((key) => this.searchCache.delete(key));
      logDebug(`🗑️ Cache cleanup: removed ${expiredKeys.length} expired entries`, {
        remainingSize: this.searchCache.size,
      });
    }
  }

  /**
   * Check cache for recent search results (5-minute TTL)
   */
  private getCachedSources(topic: string, subject?: string): string[] | null {
    const cacheKey = `${topic.toLowerCase()}:${subject || "general"}`;
    const cached = this.searchCache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      logDebug(`✅ Cache hit for topic: ${topic}`, {
        age: `${Math.round((Date.now() - cached.timestamp) / 1000)}s`,
        sourcesCount: cached.sources.length,
      });

      // ✅ Update timestamp for LRU behavior (mark as recently used)
      cached.timestamp = Date.now();
      this.searchCache.set(cacheKey, cached);

      return cached.sources;
    }

    // Clean expired cache entry if found
    if (cached) {
      this.searchCache.delete(cacheKey);
    }

    return null;
  }

  /**
   * Store search results in cache
   * ✅ IMPROVED: Only cache successful searches + proactive cleanup
   */
  private setCachedSources(topic: string, subject: string | undefined, sources: string[]): void {
    // ✅ DON'T cache empty results (failures shouldn't be cached)
    if (sources.length === 0) {
      logDebug(`Skipping cache storage for empty results (topic: ${topic})`);
      return;
    }

    const cacheKey = `${topic.toLowerCase()}:${subject || "general"}`;
    this.searchCache.set(cacheKey, { sources, timestamp: Date.now() });

    logDebug(`✅ Cached ${sources.length} sources for topic: ${topic}`, {
      cacheKey,
      ttl: `${this.CACHE_TTL / 1000}s`,
      cacheSize: this.searchCache.size,
    });

    // ✅ IMPROVED: Clean expired entries first, then evict old entries if needed
    this.cleanExpiredCacheEntries();
    this.evictOldestCacheEntries();
  }

  /**
   * ✅ NEW: Manual cache invalidation for specific topic
   * Useful for refreshing stale data
   */
  invalidateCache(topic?: string, subject?: string): void {
    if (topic) {
      const cacheKey = `${topic.toLowerCase()}:${subject || "general"}`;
      const deleted = this.searchCache.delete(cacheKey);
      logDebug(
        deleted
          ? `🗑️ Invalidated cache for topic: ${topic}`
          : `⚠️ No cache entry found for topic: ${topic}`
      );
    } else {
      // Clear entire cache
      const size = this.searchCache.size;
      this.searchCache.clear();
      logDebug(`🗑️ Cleared entire cache (${size} entries removed)`);
    }
  }

  /**
   * ✅ NEW: Get cache statistics for monitoring
   */
  getCacheStats(): {
    size: number;
    maxSize: number;
    ttlSeconds: number;
    entries: Array<{ key: string; age: number; sourcesCount: number }>;
  } {
    const now = Date.now();
    const entries = Array.from(this.searchCache.entries()).map(([key, value]) => ({
      key,
      age: Math.round((now - value.timestamp) / 1000),
      sourcesCount: value.sources.length,
    }));

    return {
      size: this.searchCache.size,
      maxSize: this.MAX_CACHE_SIZE,
      ttlSeconds: this.CACHE_TTL / 1000,
      entries,
    };
  }

  /**
   * Initialize the Google Custom Search tool and HF Reranker
   *
   * ✅ UPDATED: Now uses backend proxy instead of frontend Google Search API
   * This allows search functionality without exposing API keys to browser
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      logDebug("Google search service already initialized");
      return;
    }

    try {
      // ✅ NEW: Check if backend has Google Search API configured
      const { backendAIService } = await import("./backendAIService");
      const apiConfig = await backendAIService.checkAPIConfiguration();

      if (!apiConfig.google_search) {
        logWarn("⚠️ Google Search not configured on backend - search functionality limited");
        // Still mark as initialized to allow service to function with fallbacks
        this.isInitialized = true;
        return;
      }

      // Mark as initialized - we'll use backend proxy for search
      this.isInitialized = true;
      logInfo("✅ Google Search Service initialized (using backend proxy)", {
        mode: "backend-proxy",
        googleSearchAPI: "Secured on backend",
        reranking: "Via backend HF service",
      });
    } catch (error) {
      logError("Failed to initialize search service", error);
      // Still mark as initialized to allow graceful degradation
      this.isInitialized = true;
    }
  }

  /**
   * Ensure service is initialized before use
   */
  private ensureInitialized(): void {
    if (!this.isInitialized) {
      logWarn("Google search service not initialized - initializing now");
      // Allow the service to proceed even if not explicitly initialized
      this.isInitialized = true;
    }
  }

  /**
   * Build educational search queries using semantic approach
   * Uses full topic context instead of keyword extraction to prevent irrelevant matches
   * Example: "electromagnetic waves" stays together, not split into "waves" (which could match boat waves)
   */
  buildEducationalSearchQueries(topic: string, subject?: string): string[] {
    const queries: string[] = [];

    // SEMANTIC APPROACH: Keep topic as a coherent phrase for better context
    const fullTopicPhrase = topic.trim();

    // Build context-aware exclusions based on the FULL topic, not fragmented keywords
    const contextAwareExclusions = this.buildContextAwareExclusions(topic, fullTopicPhrase);

    // Strategy 1: Full topic phrase with educational context
    // This ensures "electromagnetic waves" is searched as a unit, not individual words
    queries.push(
      `"${fullTopicPhrase}" educational explanation definition -enrollment -admission -course -program -login -error -404 ${contextAwareExclusions}`
    );

    // Strategy 2: Subject-specific search maintaining full topic integrity
    const subjectSpecificQuery = this.buildEnhancedSubjectSpecificQuery(
      topic,
      fullTopicPhrase,
      subject
    );
    if (subjectSpecificQuery) {
      queries.push(subjectSpecificQuery);
    }

    // Strategy 3: Academic/reference search with full topic context
    queries.push(
      `"${fullTopicPhrase}" academic reference study research -enrollment -admission ${contextAwareExclusions}`
    );

    logDebug("Built educational search queries (semantic mode)", {
      topic,
      fullTopicPhrase,
      subject,
      queriesCount: queries.length,
      approach: "Semantic search using full topic phrases instead of keyword fragmentation",
    });

    // Return top 2 most relevant queries
    return queries.slice(0, 2);
  }

  /**
   * Build context-aware exclusions to filter out irrelevant results
   */
  private buildContextAwareExclusions(topic: string, fullTopicPhrase: string): string {
    const exclusions: string[] = [];

    // IMPORTANT: Use domain context validation instead of hard-coded exclusions
    // This prevents false matches like "waves" → "boat waves" for "electromagnetic waves"
    const domainContexts = this.getTopicDomainContext(fullTopicPhrase);

    // Only add specific exclusions if the topic doesn't have strong domain context
    if (domainContexts.length === 0) {
      // Generic exclusions for topics without clear domain
      exclusions.push("-shopping", "-buy", "-sale", "-price");
    }

    logDebug("Built context-aware exclusions", {
      topic,
      fullTopicPhrase,
      domainContexts,
      exclusions,
      approach: "Domain context validation (not keyword-based exclusions)",
    });

    return exclusions.join(" ");
  }

  /**
   * Build enhanced subject-specific search query
   * ✅ UPDATED: Supports Philippine K-12 SHS subjects + "General Knowledge" fallback
   */
  private buildEnhancedSubjectSpecificQuery(
    topic: string,
    fullTopicPhrase: string,
    subject?: string
  ): string {
    // ✅ Philippine K-12 Senior High School subjects + general fallback
    const subjectMap: { [key: string]: string } = {
      // Core SHS Subjects
      "oral communication": "communication speech public speaking rhetoric presentation",
      "reading and writing": "reading writing essay composition grammar literature",
      "21st century literature": "literature poetry novel narrative literary analysis",
      "komunikasyon at pananaliksik": "komunikasyon pananaliksik filipino wika pagsulat",
      "pagbasa at pagsusuri": "pagbasa pagsusuri panitikan filipino teksto",
      "general mathematics": "mathematics algebra geometry trigonometry calculus",
      "statistics and probability": "statistics probability data analysis distribution",
      "earth and life science": "biology ecology ecosystem earth science geology",
      "physical science": "physics chemistry electromagnetic waves energy matter",
      "earth science": "geology earth plate tectonics rocks minerals climate",

      // General/Legacy mappings (for backward compatibility)
      "general knowledge": "educational academic reference encyclopedia",
      mathematics: "math mathematics algebra geometry calculus",
      science: "science physics chemistry biology",
      english: "english literature grammar writing",
      filipino: "filipino panitikan wika gramatika",
      "araling panlipunan": "history geography social studies Philippines",
      physics: "physics mechanics thermodynamics electromagnetism",
      chemistry: "chemistry chemical reactions elements compounds",
      biology: "biology life science organisms cells",
    };

    const subjectContext = subject ? subjectMap[subject.toLowerCase()] || subject : "";

    if (subjectContext) {
      logDebug("Built subject-specific query", {
        subject,
        subjectContext,
        fullTopicPhrase: fullTopicPhrase.substring(0, 50),
      });
      return `"${fullTopicPhrase}" ${subjectContext} explanation -enrollment -admission`;
    }

    // ✅ Fallback: Use general educational query if no subject context found
    logDebug("No subject-specific context found, using general educational query", {
      subject,
      fullTopicPhrase: fullTopicPhrase.substring(0, 50),
    });
    return `"${fullTopicPhrase}" educational explanation definition -enrollment -admission`;
  }

  /**
   * Get topic domain context for semantic validation
   * Maps topics to their academic domain-specific terms
   * Used to validate that search results are from the correct domain
   */
  private getTopicDomainContext(topicPhrase: string): string[] {
    const lowerTopic = topicPhrase.toLowerCase();
    const contexts: string[] = [];

    // Physics domain
    if (
      lowerTopic.includes("electromagnetic") ||
      lowerTopic.includes("wave") ||
      lowerTopic.includes("frequency") ||
      lowerTopic.includes("spectrum")
    ) {
      contexts.push("physics", "electromagnetic", "radiation", "spectrum", "frequency");
    }

    // Chemistry domain
    if (
      lowerTopic.includes("compound") ||
      lowerTopic.includes("element") ||
      lowerTopic.includes("chemical") ||
      lowerTopic.includes("reaction") ||
      lowerTopic.includes("molecule")
    ) {
      contexts.push("chemistry", "chemical", "molecule", "atom", "reaction");
    }

    // Mathematics/Finance domain
    if (
      lowerTopic.includes("interest") ||
      lowerTopic.includes("compound interest") ||
      lowerTopic.includes("simple interest") ||
      lowerTopic.includes("investment") ||
      lowerTopic.includes("principal")
    ) {
      contexts.push("mathematics", "finance", "investment", "calculation", "formula");
    }

    // Earth Science/Geology domain
    if (
      lowerTopic.includes("rock") ||
      lowerTopic.includes("mineral") ||
      lowerTopic.includes("geological") ||
      lowerTopic.includes("sediment")
    ) {
      contexts.push("geology", "earth science", "mineral", "geological", "formation");
    }

    // Biology domain
    if (
      lowerTopic.includes("cell") ||
      lowerTopic.includes("organism") ||
      lowerTopic.includes("photosynthesis") ||
      lowerTopic.includes("ecosystem")
    ) {
      contexts.push("biology", "life science", "organism", "cellular", "biological");
    }

    logDebug("Topic domain context mapping", {
      topicPhrase,
      identifiedContexts: contexts,
      approach: "Domain-specific vocabulary for semantic validation",
    });

    return contexts;
  }

  /**
   * Check if a URL has semantic relevance to the topic
   * Uses domain context validation instead of simple keyword matching
   */
  private hasSemanticTopicRelevance(url: string, topicPhrase: string): boolean {
    const lowerUrl = url.toLowerCase();
    const topicWords = topicPhrase
      .toLowerCase()
      .split(/\s+/)
      .filter((word) => word.length > 2);

    // For multi-word topics, require ALL significant words to be present
    const allWordsPresent = topicWords.every((word) => lowerUrl.includes(word));

    // Check domain context
    const domainContexts = this.getTopicDomainContext(topicPhrase);
    const hasDomainContext =
      domainContexts.length === 0 || // No specific domain required
      domainContexts.some((context) => lowerUrl.includes(context.toLowerCase()));

    const isRelevant = allWordsPresent && hasDomainContext;

    logDebug("Semantic relevance check", {
      url,
      topicPhrase,
      allWordsPresent,
      hasDomainContext,
      domainContexts,
      isRelevant,
    });

    return isRelevant;
  }

  /**
   * Check if topic is relevant to URL
   * Uses semantic validation with domain context
   */
  private isTopicRelevant(url: string, topic: string): boolean {
    const lowerUrl = url.toLowerCase();
    const lowerTopic = topic.toLowerCase();

    // SEMANTIC VALIDATION: Check if URL contains the full topic phrase or its semantic components
    const fullTopicPhrase = topic.trim();

    // Use semantic relevance check instead of simple keyword counting
    if (this.hasSemanticTopicRelevance(url, fullTopicPhrase)) {
      logDebug("✅ TOPIC-RELEVANT URL (semantic validation)", {
        url,
        topic,
        fullTopicPhrase,
        validationType: "Semantic similarity with domain context",
      });
      return true;
    }

    logDebug("❌ URL not semantically relevant to topic", {
      url,
      topic,
      fullTopicPhrase,
      reason: "Failed semantic validation or missing domain context",
    });

    return false;
  }

  /**
   * Normalize URL for deduplication
   * Removes query parameters, fragments, and normalizes trailing slashes
   */
  private normalizeUrl(url: string): string {
    try {
      const parsedUrl = new URL(url);
      // Remove query params and fragments, normalize trailing slash
      let normalized = `${parsedUrl.protocol}//${parsedUrl.hostname}${parsedUrl.pathname}`;
      // Remove trailing slash for consistency
      normalized = normalized.replace(/\/$/, "");
      return normalized.toLowerCase();
    } catch (error) {
      // If URL parsing fails, return lowercase version
      return url.toLowerCase().replace(/\/$/, "");
    }
  }

  /**
   * Check if a URL is a duplicate based on normalized hostname and pathname
   * Ignores query parameters, fragments, and utm_ tracking parameters
   */
  private isDuplicateUrl(newUrl: string, existingUrls: string[]): boolean {
    try {
      const normalizedNew = this.normalizeUrl(newUrl);
      return existingUrls.some((existingUrl) => {
        const normalizedExisting = this.normalizeUrl(existingUrl);
        return normalizedNew === normalizedExisting;
      });
    } catch (error) {
      // Fallback to simple string comparison
      return existingUrls.some((url) => url.includes(newUrl) || newUrl.includes(url));
    }
  }

  /**
   * Deduplicate and canonicalize URLs
   * Removes same-domain duplicates and normalizes tracking parameters
   */
  private deduplicateUrls(urls: string[]): string[] {
    const seen: string[] = [];
    const result: string[] = [];

    for (const url of urls) {
      if (!this.isDuplicateUrl(url, seen)) {
        seen.push(url);
        result.push(url);
      }
    }

    logDebug("URL deduplication completed", {
      originalCount: urls.length,
      uniqueCount: result.length,
      duplicatesRemoved: urls.length - result.length,
    });

    return result;
  }

  /**
   * Filter credible sources based on domain and relevance
   */
  private isCredibleSource(url: string, topic: string): boolean {
    const lowerUrl = url.toLowerCase();

    // EXPANDED: More comprehensive credible educational domains
    // NOTE: Wikipedia removed as it's not considered credible for academic work
    const credibleDomains = [
      ".edu",
      ".gov",
      "britannica.com",
      "khanacademy.org",
      "sciencedirect.com",
      "nature.com",
      "springer.com",
      "ieee.org",
      "deped.gov.ph",
      "ched.gov.ph",
      "oxfordlearnersdictionaries.com",
      "merriam-webster.com",
      "dictionary.cambridge.org",
      // Additional quality educational sources
      "cambridge.org",
      "oxford.ac.uk",
      "nationalgeographic.com",
      "smithsonianmag.com",
      "nasa.gov",
      "usgs.gov",
      "nih.gov",
      "cdc.gov",
      "mathworld.wolfram.com",
      "arxiv.org",
      "jstor.org",
      "scholar.google.com",
      "researchgate.net",
      "academia.edu",
    ];

    const isCredible = credibleDomains.some((domain) => lowerUrl.includes(domain));

    // Block direct PDF file URLs (often not properly formatted or accessible)
    const isPDF = lowerUrl.endsWith(".pdf") || lowerUrl.includes(".pdf?");
    if (isPDF) {
      logDebug("❌ Blocked PDF file URL", {
        url,
        reason: "Direct PDF files are not credible sources",
      });
      return false;
    }

    // Check topic relevance
    const isRelevant = this.isTopicRelevant(url, topic);

    logDebug("Credible source check", {
      url,
      topic,
      isCredible,
      isRelevant,
      isPDF,
      finalDecision: isCredible && isRelevant,
    });

    return isCredible && isRelevant;
  }

  /**
   * Validate URLs for accessibility (404 detection)
   * Uses HEAD request to check if URL is reachable without downloading content
   * Returns only accessible URLs to prevent broken references
   *
   * ⚠️ NOTE: This will NOT work in browsers due to CORS restrictions.
   * For browser testing, URLs are returned as-is without validation.
   */
  private async validateUrls(
    urls: SearchResult[],
    timeoutMs: number = 5000
  ): Promise<SearchResult[]> {
    if (!urls || urls.length === 0) {
      return [];
    }

    // ⚠️ BROWSER COMPATIBILITY: Skip validation in browser environments
    // CORS policy blocks HEAD requests to external domains
    // This is a browser security feature, not a bug
    if (typeof window !== "undefined") {
      logDebug("⚠️ Skipping URL validation (browser environment - CORS restrictions)", {
        urlCount: urls.length,
        note: "URLs will be returned as-is. CORS blocks HEAD requests in browsers.",
      });
      return urls; // Return all URLs without validation
    }

    logDebug("Starting URL validation", {
      urlCount: urls.length,
      timeout: timeoutMs,
    });

    // Validate URLs in parallel with timeout protection
    const validationPromises = urls.map(async (result) => {
      try {
        // Create HEAD request with timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        const response = await fetch(result.url, {
          method: "HEAD",
          signal: controller.signal,
          redirect: "follow", // Follow redirects
        });

        clearTimeout(timeoutId);

        // Check if URL is accessible (status 200-399)
        const isAccessible = response.ok || (response.status >= 200 && response.status < 400);

        if (!isAccessible) {
          logWarn("URL not accessible", {
            url: result.url,
            status: response.status,
            statusText: response.statusText,
          });
          return null;
        }

        logDebug("URL validated successfully", {
          url: result.url,
          status: response.status,
        });

        return result;
      } catch (error: any) {
        // CORS errors are expected in browser - treat as "probably valid"
        // Only reject on actual network failures (404, 500, timeout)
        if (
          error.message?.includes("CORS") ||
          error.message?.includes("No 'Access-Control-Allow-Origin'")
        ) {
          logDebug("URL blocked by CORS (treating as valid - CORS is expected in browser)", {
            url: result.url,
          });
          return result; // Keep URL - CORS doesn't mean it's broken
        }

        // Timeout or actual network error - reject
        logWarn("URL validation failed", {
          url: result.url,
          error: error.message || "Unknown error",
          type: error.name === "AbortError" ? "timeout" : "network_error",
        });
        return null;
      }
    });

    // Wait for all validations to complete
    const validationResults = await Promise.all(validationPromises);

    // Filter out failed validations (null values)
    const validUrls = validationResults.filter((result): result is SearchResult => result !== null);

    logInfo("URL validation completed", {
      originalCount: urls.length,
      validCount: validUrls.length,
      removedCount: urls.length - validUrls.length,
      successRate: `${((validUrls.length / urls.length) * 100).toFixed(1)}%`,
    });

    return validUrls;
  }

  /**
   * Execute search with retry logic
   */
  /**
   * Execute search with retry logic AND timeout protection
   */
  /**
   * Execute Google Search using backend proxy (SECURE)
   *
   * ✅ UPDATED: Now uses backend API instead of frontend Google Search
   * This prevents exposing API keys to the browser
   *
   * Pipeline: Frontend → Backend Proxy → Google Custom Search API → Frontend
   */
  private async executeSearchWithRetry(
    query: string,
    maxRetries: number = 2
  ): Promise<SearchResult[]> {
    this.ensureInitialized();

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        logDebug(`Backend search attempt ${attempt}/${maxRetries}`, { query });

        const sanitizedQuery = sanitizeSearchQuery(query);

        // ✅ NEW: Use backend proxy instead of frontend LangChain
        const { backendAIService } = await import("./backendAIService");

        // Add timeout protection (15 seconds per search attempt)
        // Backend expects array of queries, pass single query as array
        const searchPromise = backendAIService.googleSearch([sanitizedQuery], 10);
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new Error(`Google Search timeout after 15s (attempt ${attempt}/${maxRetries})`));
          }, 15000); // 15 second timeout
        });

        const backendResults = await Promise.race([searchPromise, timeoutPromise]);

        // Backend returns GoogleSearchResult[] (already flattened and structured)
        const results: SearchResult[] = backendResults.map((item) => ({
          url: item.url,
          title: item.title,
          snippet: item.snippet,
        }));

        logDebug(`Backend search completed (attempt ${attempt})`, {
          query: sanitizedQuery,
          resultsCount: results.length,
          source: "backend-proxy",
        });

        return results;
      } catch (error) {
        logWarn(`Backend search attempt ${attempt} failed`, { query, error });
        if (attempt === maxRetries) {
          logError("All backend search attempts failed", { query, error });
          return [];
        }
        // Wait before retry (exponential backoff)
        await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
      }
    }

    return [];
  }

  /**
   * Search credible sources for a single topic with semantic reranking
   * ✅ UPDATED: Uses backend AI service for reranking + validation
   * ✅ OPTIMIZED: Checks cache first (5-min TTL) before hitting APIs
   * Pipeline: Cache Check → Google Search → Dedupe → Filter → Backend Rerank + Validate → Top 5
   */
  async searchTopicSources(topic: string, subject?: string): Promise<string[]> {
    const startTime = performance.now();
    console.log(`\n${"=".repeat(80)}`);
    console.log(`🔬 [LANGCHAIN RETRIEVER] Started at ${new Date().toLocaleTimeString()}`);
    console.log(`   Topic: ${topic}`);
    console.log(`   Subject: ${subject || "not specified"}`);

    this.ensureInitialized();

    // ✅ OPTIMIZATION: Check cache first (instant return if hit)
    const cacheStart = performance.now();
    const cachedSources = this.getCachedSources(topic, subject);
    const cacheDuration = performance.now() - cacheStart;

    if (cachedSources !== null) {
      console.log(
        `   ✅ Cache HIT (${cacheDuration.toFixed(0)}ms) - ${cachedSources.length} sources`
      );
      const totalDuration = performance.now() - startTime;
      console.log(`✅ [LANGCHAIN RETRIEVER] Completed in ${totalDuration.toFixed(0)}ms (cached)`);
      console.log(`${"=".repeat(80)}\n`);
      return cachedSources;
    }
    console.log(`   ⚠️ Cache MISS (${cacheDuration.toFixed(0)}ms) - fetching from Google...`);

    try {
      const searchQueries = this.buildEducationalSearchQueries(topic, subject);
      console.log(`   📝 Built ${searchQueries.length} search queries`);
      for (let i = 0; i < searchQueries.length; i++) {
        console.log(`      ${i + 1}. ${searchQueries[i]}`);
      }

      const allResults: SearchResult[] = [];

      // Execute searches in parallel for better performance
      console.log(`   🚀 Executing ${searchQueries.length} searches in parallel...`);
      const searchStart = performance.now();
      const searchPromises = searchQueries.map((query) => this.executeSearchWithRetry(query));
      const searchResults = await Promise.all(searchPromises);
      const searchDuration = performance.now() - searchStart;
      console.log(`   ✅ Google searches completed in ${searchDuration.toFixed(0)}ms`);

      // Flatten results
      searchResults.forEach((results) => allResults.push(...results));
      console.log(`   📊 Total raw results: ${allResults.length}`);

      // ✅ FIX: Log if Google Search returned empty results (helps diagnose API issues)
      if (allResults.length === 0) {
        console.warn(`   ⚠️ Google Search returned 0 results`);
        logWarn("⚠️ Google Search returned 0 results", {
          topic,
          subject,
          queries: searchQueries,
          possibleReasons: [
            "Google Search API quota exceeded",
            "Network connectivity issues",
            "Backend not configured with Google API keys",
          ],
        });
        return []; // Return early, no point processing empty results
      }

      // ✅ STEP 1: Deduplicate URLs early to prevent duplicate processing
      console.log(`   🔄 Deduplicating URLs...`);
      const dedupeStart = performance.now();
      const deduplicatedResults = this.deduplicateUrls(allResults.map((r) => r.url)).map(
        (url) => allResults.find((r) => r.url === url)!
      );
      const dedupeDuration = performance.now() - dedupeStart;
      console.log(
        `   ✅ Deduplicated to ${deduplicatedResults.length} unique URLs (${dedupeDuration.toFixed(
          0
        )}ms)`
      );

      // ✅ STEP 2: Filter credible sources BEFORE reranking (saves API calls)
      console.log(`   🔍 Filtering credible sources...`);
      const filterStart = performance.now();
      const uniqueUrls = new Set<string>();
      const credibleResults: SearchResult[] = [];

      for (const result of deduplicatedResults) {
        if (!uniqueUrls.has(result.url) && this.isCredibleSource(result.url, topic)) {
          uniqueUrls.add(result.url);
          credibleResults.push(result);
        }
      }
      const filterDuration = performance.now() - filterStart;
      console.log(
        `   ✅ Filtered to ${credibleResults.length} credible sources (${filterDuration.toFixed(
          0
        )}ms)`
      );

      logDebug(`✅ Filtered ${credibleResults.length} credible sources (before backend)`, {
        topic,
        totalResults: allResults.length,
        afterDeduplication: deduplicatedResults.length,
        afterCredibilityFilter: credibleResults.length,
        filteredOut: deduplicatedResults.length - credibleResults.length,
      });

      // ✅ STEP 3: Backend AI Service - Combined Rerank + Validate (with timeout protection)
      // This fixes ALL critical issues:
      // - Provider locked to hf-inference (no more novita)
      // - URL validation without CORS blocking
      // - Hard truncate to top 5 sources
      console.log(`   🚀 Calling backend for reranking + validation (60s timeout)...`);
      logInfo(`🚀 Calling backend AI service for rerank + validate`);

      // ✅ Dynamic import to avoid circular dependency
      const { backendAIService } = await import("./backendAIService");

      // ✅ OPTIMIZATION: Add explicit timeout wrapper (60s max for backend processing)
      const rerankStart = performance.now();
      const backendPromise = backendAIService.rerankAndValidate(
        topic,
        credibleResults,
        5 // Hard cap at 5 sources
      );

      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error(`Backend rerank timeout after 60s for topic: ${topic}`));
        }, 60000); // 60 second explicit timeout for backend operations
      });

      const rerankedSources = await Promise.race([backendPromise, timeoutPromise]).catch(
        (error) => {
          const rerankDuration = performance.now() - rerankStart;
          console.error(`   ❌ Backend rerank failed after ${rerankDuration.toFixed(0)}ms:`, error);
          logError("Backend rerank failed, using credibility-only fallback", error);
          // Fallback: Return top 5 credible sources (no reranking)
          return credibleResults.slice(0, 5).map((result) => ({
            url: result.url,
            score: 0.5,
            title: result.title,
            snippet: result.snippet,
          }));
        }
      );

      const rerankDuration = performance.now() - rerankStart;
      console.log(
        `   ✅ Backend rerank completed in ${rerankDuration.toFixed(0)}ms - ${
          rerankedSources.length
        } sources`
      );

      // Extract URLs from reranked sources
      const finalSources = rerankedSources.map((s) => s.url);

      logSuccess(`✅ Backend pipeline complete: ${finalSources.length} sources ready`, {
        topic,
        beforeBackend: credibleResults.length,
        afterBackend: finalSources.length,
        topScores: rerankedSources.slice(0, 5).map((s) => s.score?.toFixed(3) || "N/A"),
        method: "backend_ai_service",
      });

      // ✅ OPTIMIZATION: Cache successful results (5-min TTL)
      console.log(`   💾 Caching ${finalSources.length} sources (5-min TTL)...`);
      if (finalSources.length > 0) {
        this.setCachedSources(topic, subject, finalSources);
        console.log(`   ✅ Cached successfully`);
      } else {
        console.log(`   ⚠️ Skipping cache (empty results)`);
      }

      const totalDuration = performance.now() - startTime;
      console.log(
        `✅ [LANGCHAIN RETRIEVER] Completed in ${totalDuration.toFixed(0)}ms - ${
          finalSources.length
        } sources`
      );
      console.log(`${"=".repeat(80)}\n`);

      return finalSources;
    } catch (error) {
      const totalDuration = performance.now() - startTime;
      console.error(`❌ [LANGCHAIN RETRIEVER] Failed after ${totalDuration.toFixed(0)}ms:`, error);
      console.log(`${"=".repeat(80)}\n`);
      logError("Error searching topic sources", { topic, error });

      // Fallback: Return empty array (caller should handle this)
      return [];
    }
  }

  /**
   * Search credible sources for multiple topics (PARALLEL execution with EARLY TERMINATION)
   * ✅ OPTIMIZATION: Stops early if we get 5+ high-quality sources from first topic
   */
  async searchCredibleSources(topics: string[], subject?: string): Promise<string[]> {
    this.ensureInitialized();

    if (!topics || topics.length === 0) {
      logWarn("No topics provided for search");
      return [];
    }

    try {
      logInfo(`🔍 Starting parallel search for ${topics.length} topics`, { topics, subject });

      // ✅ OPTIMIZATION: Early termination strategy
      // Search first topic - if it returns 5+ sources, we're done (saves 50% time)
      const firstTopicSources = await this.searchTopicSources(topics[0], subject);

      if (firstTopicSources.length >= 5) {
        logSuccess(
          `✅ Early termination: First topic returned ${firstTopicSources.length} sources (target met)`,
          {
            topic: topics[0],
            sourcesFound: firstTopicSources.length,
            timeSaved: "~50% (skipped remaining topics)",
          }
        );
        return firstTopicSources;
      }

      // If first topic returned <5 sources, search remaining topics in parallel
      if (topics.length > 1) {
        logDebug(
          `First topic returned ${firstTopicSources.length} sources, searching remaining topics...`
        );

        const remainingTopics = topics.slice(1);
        const searchPromises = remainingTopics.map((topic) =>
          this.searchTopicSources(topic, subject)
        );
        const remainingResults = await Promise.all(searchPromises);

        // Combine all sources
        const allSources = [firstTopicSources, ...remainingResults].flat();
        const uniqueSources = Array.from(new Set(allSources));

        logInfo(`✅ Parallel search completed`, {
          topicsSearched: topics.length,
          totalSources: uniqueSources.length,
        });

        return uniqueSources;
      }

      return firstTopicSources;
    } catch (error) {
      logError("Error in parallel credible sources search", { topics, error });
      return [];
    }
  }

  /**
   * Enhanced search for summary generation
   * Optimized for Philippine K-12 curriculum
   */
  async searchCredibleSourcesEnhanced(topics: string[], subject?: string): Promise<string[]> {
    this.ensureInitialized();

    if (!topics || topics.length === 0) {
      return [];
    }

    try {
      logInfo(`🔍 Enhanced search for summary (${topics.length} topics)`, { topics, subject });

      // Use parallel search
      const sources = await this.searchCredibleSources(topics, subject);

      logInfo(`✅ Enhanced search completed for summary`, {
        topicsCount: topics.length,
        sourcesFound: sources.length,
      });

      return sources;
    } catch (error) {
      logError("Error in enhanced search for summary", { topics, error });
      return [];
    }
  }

  /**
   * Search credible sources for flashcard generation with semantic reranking
   * Uses TIER 1 (educational queries) and TIER 2 (standard search) approach
   * Pipeline: Google Search → Filter → HF Rerank → Top K
   */
  async searchCredibleSourcesForFlashcards(topics: string[], subject?: string): Promise<string[]> {
    this.ensureInitialized();

    if (!topics || topics.length === 0) {
      return [];
    }

    try {
      logInfo(`🔍 Flashcard search (TIER 1 + TIER 2 + Reranking) for ${topics.length} topics`, {
        topics,
        subject,
      });

      // ✅ FIX #5: ENHANCED debug logging for search quality monitoring
      logDebug("🔍 FLASHCARD SEARCH QUALITY METRICS", {
        totalTopics: topics.length,
        topicsSearched: topics,
        detectedSubject: subject,
        pipeline: "Google Search API → LangChain Retriever → HF Reranker",
      });

      // TIER 1: Educational-specific queries (PARALLEL)
      // ⚡ OPTIMIZATION 6: Limit to top 3 topics only (prevent excessive API calls)
      const limitedTopics = topics.slice(0, 3);
      logDebug(
        `⚡ Performance: Limited to ${limitedTopics.length} topics (from ${topics.length})`,
        {
          originalTopics: topics,
          limitedTopics,
        }
      );

      const tier1Promises = limitedTopics.map(async (topic) => {
        const educationalQueries = this.buildEducationalSearchQueries(topic, subject);

        // ✅ FIX #5: Log queries being used
        logDebug(`Building queries for topic: "${topic}"`, {
          queriesBuilt: educationalQueries,
          approach: "Full topic phrases (semantic search)",
        });

        const searchPromises = educationalQueries.map((query) =>
          this.executeSearchWithRetry(query)
        );
        const results = await Promise.all(searchPromises);
        return { topic, results: results.flat() };
      });

      const tier1Results = await Promise.all(tier1Promises);

      // ✅ FIX #5: Log raw search results before filtering
      const totalRawResults = tier1Results.reduce((sum, r) => sum + r.results.length, 0);
      logDebug("📊 TIER 1 RAW SEARCH RESULTS", {
        totalResults: totalRawResults,
        resultsPerTopic: tier1Results.map((r) => ({
          topic: r.topic,
          count: r.results.length,
        })),
      });

      // Filter credible sources from TIER 1 (keep SearchResult format for reranking)
      const credibleTier1WithTopics: Array<{
        topic: string;
        sources: SearchResult[];
      }> = tier1Results.map((topicResult) => {
        const deduplicated = this.deduplicateUrls(topicResult.results.map((r) => r.url)).map(
          (url) => topicResult.results.find((r) => r.url === url)!
        );
        return {
          topic: topicResult.topic,
          sources: deduplicated.filter((result) =>
            this.isCredibleSource(result.url, topicResult.topic)
          ),
        };
      });

      // ✅ FIX #5: Log credibility filtering results
      const totalAfterCredibilityFilter = credibleTier1WithTopics.reduce(
        (sum, t) => sum + t.sources.length,
        0
      );
      logDebug("📊 AFTER CREDIBILITY FILTER", {
        before: totalRawResults,
        after: totalAfterCredibilityFilter,
        filtered: totalRawResults - totalAfterCredibilityFilter,
        filterRate: `${Math.round(
          ((totalRawResults - totalAfterCredibilityFilter) / totalRawResults) * 100
        )}%`,
      });

      // ✅ STEP 3: Backend AI Service - Rerank + Validate per topic (PARALLEL)
      logInfo(`🚀 Calling backend AI service for flashcard reranking`);

      // ✅ Dynamic import to avoid circular dependency
      const { backendAIService } = await import("./backendAIService");

      const rerankedTier1 = await Promise.all(
        credibleTier1WithTopics.map(async (topicData) => {
          const reranked = await backendAIService.rerankAndValidate(
            topicData.topic,
            topicData.sources,
            3 // Request top 3 per topic
          );
          return reranked;
        })
      );

      // Flatten reranked sources
      const tier1Sources = rerankedTier1.flat().map((s) => s.url);
      const uniqueTier1 = Array.from(new Set(tier1Sources));

      logSuccess(`✅ TIER 1 completed: ${uniqueTier1.length} reranked sources`, {
        topicsCount: topics.length,
        avgSourcesPerTopic: (uniqueTier1.length / topics.length).toFixed(1),
        method: "backend_ai_service",
      });

      // ✅ FIX #5: Enhanced logging for final source selection
      logDebug("📊 FINAL FLASHCARD SOURCE SELECTION", {
        tier1SourceCount: uniqueTier1.length,
        tier1Sources: uniqueTier1.slice(0, 3),
        willUseTier2: uniqueTier1.length < 3,
        targetSourceCount: "3-5 sources (reduced from 8)",
      });

      // ✅ FIX #6: Cap to MIN 3, MAX 5 sources (reduced from 8)
      if (uniqueTier1.length >= 3) {
        const cappedSources = uniqueTier1.slice(0, 5);
        logInfo(`✅ Flashcard sources capped to ${cappedSources.length} (target: 3-5)`, {
          originalCount: uniqueTier1.length,
          finalCount: cappedSources.length,
        });
        return cappedSources;
      }

      // TIER 2: Standard search for remaining topics (PARALLEL)
      logInfo("TIER 1 insufficient, executing TIER 2 search");
      const tier2Sources = await this.searchCredibleSources(topics, subject);

      // Combine and deduplicate
      const allSources = [...uniqueTier1, ...tier2Sources];
      const uniqueSources = Array.from(new Set(allSources));

      // ✅ FIX #6: Cap to MIN 3, MAX 5 sources (reduced from 8)
      const cappedSources = uniqueSources.slice(0, 5);

      logInfo(`✅ Flashcard search completed (TIER 1 + TIER 2 + Reranking)`, {
        tier1Count: uniqueTier1.length,
        tier2Count: tier2Sources.length,
        totalUnique: uniqueSources.length,
        finalSourceCount: cappedSources.length,
        targetRange: "3-5 sources (reduced from 8)",
      });

      return cappedSources;
    } catch (error) {
      logError("Error in flashcard credible sources search", { topics, error });
      return [];
    }
  }
}

// Export singleton instance creator
let googleSearchServiceInstance: GoogleSearchService | null = null;

export function createGoogleSearchService(config: GoogleSearchConfig): GoogleSearchService {
  if (!googleSearchServiceInstance) {
    googleSearchServiceInstance = new GoogleSearchService(config);
  }
  return googleSearchServiceInstance;
}

export function getGoogleSearchService(): GoogleSearchService {
  if (!googleSearchServiceInstance) {
    // Auto-initialize with default config to prevent initialization errors
    googleSearchServiceInstance = new GoogleSearchService({
      apiKey: import.meta.env.VITE_GOOGLE_SEARCH_API_KEY || "",
      searchEngineId: import.meta.env.VITE_GOOGLE_SEARCH_ENGINE_ID || "",
    });
  }
  return googleSearchServiceInstance;
}

// Create the Proxy lazily - don't create at module level
let _proxyCache: GoogleSearchService | null = null;

function getProxyInstance(): GoogleSearchService {
  if (!_proxyCache) {
    _proxyCache = new Proxy({} as GoogleSearchService, {
      get(target, prop) {
        return getGoogleSearchService()[prop as keyof GoogleSearchService];
      },
    }) as GoogleSearchService;
  }
  return _proxyCache;
}

// Export the Proxy via a getter to delay its creation
export const googleSearchService: GoogleSearchService = getProxyInstance();
